import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { request } from "@/lib/request";
import { emptyStorageUser } from "@/lib/storage";
import { User } from "@/lib/types";
import { cn } from "@/lib/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Loader2Icon, TriangleAlertIcon } from "lucide-react";
import { memo, useCallback, useEffect, useState } from "react";
import { z } from "zod";

const searchSchema = z.object({
  token: z.string().nonempty().catch(""),
});

export const Route = createFileRoute("/account/delete")({
  validateSearch: zodValidator(searchSchema),
  loader: async () => {
    const accessToken = localStorage.getItem("unideck.auth.accessToken");
    return { accessToken };
  },
  component: memo(RouteComponent),
});

function RouteComponent() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const { accessToken } = Route.useLoaderData();

  const [passwordVerified, setPasswordVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");

  const fetchUser = async () => {
    const res = await request
      .get(`/user/info`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .catch(async err => {
        if (err.response?.status === 401) {
          await emptyStorageUser();
          navigate({
            to: "/",
            search: {
              change_password_token: undefined,
              reset_password_token: undefined,
            },
          });
        }

        throw new Error(
          err.response?.data.message
            ? err.response?.data.message
            : "Failed to load user profile",
        );
      });

    setUser(res.data.data);
  };

  const deleteUser = async () => {
    await request
      .delete(`/auth/user`, {
        data: { token },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .catch(err => {
        throw new Error(
          err.response?.data.message
            ? err.response?.data.message
            : "Failed to delete user.",
        );
      });
  };

  const loadUserInfo = async () => {
    if (!token || !accessToken) {
      navigate({
        to: "/",
        search: {
          change_password_token: undefined,
          reset_password_token: undefined,
        },
      });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await fetchUser();
    } catch {
      setError("Failed to load user profile");
    } finally {
      setLoading(false);
    }
  };

  const verifyPassword = useCallback(async () => {
    if (!user) {
      return;
    }

    await request
      .post(`/auth/login`, {
        email: user.email,
        password,
      })
      .catch(err => {
        throw new Error(
          err.response?.data.message
            ? err.response?.data.message
            : "Failed to verify password.",
        );
      });
  }, [user, password]);

  const checkPassword = async () => {
    if (!password) {
      setError("Please enter your password");
      return;
    }

    setError(null);
    setPending(true);
    try {
      await verifyPassword();
      setPasswordVerified(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "An error occurred");
    } finally {
      setPending(false);
    }
  };

  const confirmDelete = async () => {
    if (!passwordVerified) {
      return;
    }

    setError(null);
    setPending(true);
    try {
      await deleteUser();
      await emptyStorageUser();
      navigate({
        to: "/",
        search: {
          change_password_token: undefined,
          reset_password_token: undefined,
        },
      });
      setPasswordVerified(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "An error occurred");
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    loadUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen w-screen items-center justify-center bg-background">
      <div className="flex w-[500px] flex-col gap-4 rounded-xl border border-border bg-card p-4 shadow-xl">
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-bold text-white">Deleting Account</h1>
          <Separator />
        </div>
        {loading ? (
          <>
            <Skeleton className="h-9 rounded-lg" />
            <Skeleton className="h-9 rounded-lg" />
            <div className="flex justify-end gap-4">
              <Skeleton className="h-9 w-[230px] rounded-lg" />
            </div>
          </>
        ) : !user ? (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {accessToken ? (
                error || "An error occurred while loading your profile"
              ) : (
                <div className="flex flex-col gap-4">
                  You are not logged in
                  <div className="flex justify-center">
                    <Button variant="default" size="sm" asChild>
                      <Link
                        to="/"
                        search={{
                          change_password_token: undefined,
                          reset_password_token: undefined,
                        }}
                      >
                        Go back to UniDeck
                      </Link>
                    </Button>
                  </div>
                </div>
              )}
            </AlertDescription>
          </Alert>
        ) : !passwordVerified ? (
          <>
            <p className="text-sm text-muted-foreground">
              Please enter your password to confirm the deletion of your
              account.
            </p>
            <div className="flex items-center justify-between gap-8">
              <Label>Email</Label>
              <Input
                className="w-[350px]"
                type="email"
                disabled
                value={user.email}
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-8">
                <Label>Password</Label>
                <Input
                  className={cn("w-[350px]", {
                    "border-red-500": !!error,
                  })}
                  type="password"
                  placeholder="Please enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </div>
              {error ? (
                <div className="flex justify-end">
                  <p className="w-[350px] text-xs text-red-500">{error}</p>
                </div>
              ) : null}
            </div>
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="destructive"
                disabled={pending}
                onClick={checkPassword}
              >
                {pending ? (
                  <>
                    <Loader2Icon className="animate-spin" />
                    Checking
                  </>
                ) : (
                  <>
                    <TriangleAlertIcon />
                    Proceed
                  </>
                )}
              </Button>
              <Button type="button" variant="default" asChild>
                <Link
                  to="/"
                  search={{
                    change_password_token: undefined,
                    reset_password_token: undefined,
                  }}
                >
                  Cancel
                </Link>
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-red-500">
              Please proceed carefully. This action is irreversible. By deleting
              your account, all your data will be permanently removed.
            </p>
            <div className="flex gap-4">
              <Button
                type="button"
                variant="destructive"
                disabled={pending}
                onClick={confirmDelete}
              >
                {pending ? (
                  <>
                    <Loader2Icon className="animate-spin" />
                    Checking
                  </>
                ) : (
                  <>
                    <TriangleAlertIcon />
                    Delete Account
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="default"
                asChild
                className="flex-grow"
              >
                <Link
                  to="/"
                  search={{
                    change_password_token: undefined,
                    reset_password_token: undefined,
                  }}
                >
                  Cancel
                </Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
